import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { LogOut, RefreshCw, ScanLine, LayoutDashboard, FileText, Bell, History, Building2, Users as UsersIcon, KeyRound, Settings as SettingsIcon, Boxes, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { api, formatRelative, ROLE_LABELS, isThirdParty, canEditAll } from "../lib/api";
import { toast } from "sonner";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { MentionsBell } from "./MentionsBell";

const FLOWLINE_LOGO = "/flowline.png";

export const TopBar = ({ onSync }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(new Date().toISOString());
  const [showChangePw, setShowChangePw] = useState(false);

  const handleSync = async () => {
    if (!canEditAll(user)) {
      toast.message("Sync available to WeLove operations users.");
      return;
    }
    setSyncing(true);
    try {
      const r = await api.post("/bath-records/sync-all");
      setLastSync(r.data.synced_at);
      toast.success(`Synced ${r.data.synced_count} open records from Brightpearl`);
      onSync && onSync();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const isGiavani = isThirdParty(user);
  const logo = FLOWLINE_LOGO;

  const navItems = isGiavani
    ? [{ to: "/third-party", icon: LayoutDashboard, label: "My Assignments" }]
    : [
        { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { to: "/scanner", icon: ScanLine, label: "Scanner" },
        ...(["admin", "office"].includes(user?.role) ? [
          { to: "/skus", icon: Boxes, label: "SKUs" },
        ] : []),
        { to: "/reports", icon: FileText, label: "Reports" },
        { to: "/audit", icon: History, label: "Audit" },
        { to: "/notifications", icon: Bell, label: "Notifications" },
        ...(user?.role === "admin" ? [
          { to: "/users", icon: UsersIcon, label: "Users" },
          { to: "/settings/roles", icon: ShieldCheck, label: "Roles" },
          { to: "/settings", icon: SettingsIcon, label: "Settings" },
        ] : []),
      ];

  return (
    <header className="sticky top-0 z-30 w-full bg-white border-b border-zinc-200">
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-6 min-w-0">
            <Link to={isGiavani ? "/third-party" : "/dashboard"} className="flex items-center gap-3" data-testid="topbar-home">
              <img src={logo} alt="Flowline" className="h-9 w-9 object-contain" />
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="font-heading font-extrabold text-base tracking-tighter text-zinc-950">
                  Flowline
                </span>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {user?.company || "Operations"} · by Soubor
                </span>
              </div>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((n) => {
                const active = location.pathname === n.to;
                const Icon = n.icon;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.to.replace("/", "")}`}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      active
                        ? "bg-zinc-100 text-zinc-950"
                        : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                    }`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={2} />
                    <span>{n.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {!isGiavani && (
              <div className="hidden lg:flex items-center gap-2 text-xs text-zinc-500">
                <span className={`w-1.5 h-1.5 rounded-full ${syncing ? "bg-amber-500 pulse-dot" : "bg-emerald-500"}`} />
                <span className="font-mono">
                  {syncing ? "Syncing…" : `Last sync: ${formatRelative(lastSync)}`}
                </span>
              </div>
            )}

            {!isGiavani && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
                className="border-zinc-200 hidden sm:inline-flex"
                data-testid="topbar-sync-button"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
                Sync
              </Button>
            )}

            <MentionsBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2 py-1.5 hover:bg-zinc-50"
                  data-testid="user-menu-trigger"
                >
                  <div className="h-7 w-7 rounded-md bg-zinc-900 text-white flex items-center justify-center text-xs font-bold">
                    {user?.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </div>
                  <div className="hidden md:flex flex-col items-start leading-tight pr-1">
                    <span className="text-xs font-semibold text-zinc-950">{user?.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {ROLE_LABELS[user?.role]} · {user?.company}
                    </span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-semibold">{user?.name}</span>
                    <span className="text-xs text-zinc-500 font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled className="text-xs">
                  <Building2 className="h-3.5 w-3.5 mr-2" />
                  {user?.company} · {ROLE_LABELS[user?.role]}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowChangePw(true)} data-testid="change-password-menu">
                  <KeyRound className="h-3.5 w-3.5 mr-2" />
                  Change password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/me/notifications")} data-testid="notification-prefs-menu">
                  <Bell className="h-3.5 w-3.5 mr-2" />
                  My notifications
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  data-testid="logout-button"
                >
                  <LogOut className="h-3.5 w-3.5 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 overflow-x-auto pb-2 -mx-1">
          {navItems.map((n) => {
            const active = location.pathname === n.to;
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replace("/", "")}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                  active ? "bg-zinc-900 text-white" : "text-zinc-600 bg-zinc-50"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {n.label}
              </Link>
            );
          })}
        </nav>
      </div>
      <ChangePasswordDialog open={showChangePw} onOpenChange={setShowChangePw} />
    </header>
  );
};
